function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
import { css, css as _css } from '@emotion/react';
import _ from 'lodash';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconSearchOutlined from '~icons/ant-design/search-outlined';
import IconX from '~icons/bx/x';
import { HighlightText } from '@arc/frontend/ui-common';
import { su, theme } from '@arc/frontend/ui-theme';
import { useForwardingRef, useHighlightMeta } from '@arc/frontend/util-common';
import { tokenizeSearchString } from '@arc/shared/util-js';
import { FormControl } from '../FormControl';
import { Input } from './Input';
const collapsedWidth = 44;
const collapsedWidthWithEndAdorn = 78;
const collapsedWidthWithClear = 100;

/**
 * If option is 'always', always apply style
 * If option is 'media-query', only apply style in
 * the query
 */
const collapsibleOption = (collapsible, style) => {
  if (collapsible === 'always') {
    return style;
  }
  if (collapsible === 'media-query') {
    return {
      [`@media (max-width: ${theme.screens.xl})`]: style
    };
  }
  if (collapsible === 'never') {
    return null;
  }
  return {
    [`@media (max-width: ${collapsible.queryWidth})`]: style
  };
};
const styles = {
  positionAnchor: (collapsible, showEndAdornment) => /*#__PURE__*/css(collapsibleOption(collapsible, /*#__PURE__*/css({
    position: 'relative',
    height: '2rem',
    minWidth: showEndAdornment ? collapsedWidthWithEndAdorn : collapsedWidth
  }, process.env.NODE_ENV === "production" ? "" : ";label:positionAnchor;")), "max-width:100%;" + (process.env.NODE_ENV === "production" ? "" : ";label:positionAnchor;")),
  positionContainer: collapsible => /*#__PURE__*/css("position:relative;right:0px;", collapsibleOption(collapsible, {
    position: 'absolute'
  }), process.env.NODE_ENV === "production" ? "" : ";label:positionContainer;"),
  container: (collapsible, expanded, showEndAdornment, value, customWidth) => {
    const width = customWidth && expanded ? customWidth : expanded ? 320 : showEndAdornment && value ? collapsedWidthWithClear : showEndAdornment ? collapsedWidthWithEndAdorn : collapsedWidth;
    return /*#__PURE__*/css(su.roundedFull, "height:2rem;transition:width 0.3s ease-in-out;", collapsibleOption(collapsible, /*#__PURE__*/css({
      width
    }, process.env.NODE_ENV === "production" ? "" : ";label:container;")), process.env.NODE_ENV === "production" ? "" : ";label:container;");
  },
  input: (collapsible, expanded, showEndAdornment) => /*#__PURE__*/css("padding:0px;margin-left:0.5rem;margin-right:1rem;", collapsibleOption(collapsible, !expanded && showEndAdornment && {
    minWidth: '0px',
    margin: 0
  }), process.env.NODE_ENV === "production" ? "" : ";label:input;"),
  endAdornment: (collapsible, expanded, showEndAdornment) => /*#__PURE__*/css(su.spaceX('0.25rem'), "display:flex;align-items:center;", collapsibleOption(collapsible, /*#__PURE__*/css(!expanded && !showEndAdornment && {
    visibility: 'hidden',
    pointerEvents: 'none'
  }, !expanded && showEndAdornment && {
    marginLeft: '0.5rem'
  }, process.env.NODE_ENV === "production" ? "" : ";label:endAdornment;")), process.env.NODE_ENV === "production" ? "" : ";label:endAdornment;"),
  clearIcon: (value, hideClear) => /*#__PURE__*/css("margin-right:-0.25rem;", (!value || hideClear) && {
    visibility: 'hidden',
    pointerEvents: 'none'
  }, process.env.NODE_ENV === "production" ? "" : ";label:clearIcon;")
};
export function InputSearch(props) {
  const [expanded, setExpanded] = useState(false);
  return <InputSearchUncontrolledExpand {...props} expanded={expanded} setExpanded={setExpanded} />;
}
var _ref = process.env.NODE_ENV === "production" ? {
  name: "e0dnmk",
  styles: "cursor:pointer"
} : {
  name: "zjfq51-input",
  styles: "cursor:pointer;label:input;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
var _ref2 = process.env.NODE_ENV === "production" ? {
  name: "jz59hy",
  styles: "padding:0.22rem 0.22rem;max-height:32px;transform:translateY(-50%);position:absolute;margin-left:1.5rem;margin-right:1.5rem;pointer-events:none;top:50%"
} : {
  name: "1kbyyts-input",
  styles: "padding:0.22rem 0.22rem;max-height:32px;transform:translateY(-50%);position:absolute;margin-left:1.5rem;margin-right:1.5rem;pointer-events:none;top:50%;label:input;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
/**
 * By default, most pages do not need to control the expanded
 * state of the InputSearch. But for cases where the input bar
 * is default collapsed, there are cases where the parent will
 * want to manually control the expanded state.
 */
export const InputSearchUncontrolledExpand = forwardRef(
// eslint-disable-next-line @typescript-eslint/no-shadow
function InputSearchUncontrolledExpand(props, forwardedRef) {
  const {
    value
  } = props;
  const {
    stacked,
    handleClear,
    hideClear = false,
    placeholder,
    barcodeScanner,
    collapsible = 'never',
    helperText,
    expanded,
    setExpanded,
    forceShowEndAdornment = false,
    customWidth,
    ...inputProps
  } = props;
  const {
    t
  } = useTranslation();
  const {
    highlightedText
  } = useHighlightMeta();
  const isClearing = useRef(false);
  const iRef = useRef(null);
  const inputRef = useForwardingRef(null, [iRef, forwardedRef]);
  useEffect(() => {
    /**
     * In the event that the input is collapsed by controlling
     * component, make sure to blur the input
     */
    if (!expanded) {
      inputRef.current?.blur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);
  const input = <Input ref={inputRef} startAdornment={<>
						<div onClick={() => inputRef?.current?.focus()} css={_ref} data-testid="search-icon">
							<IconSearchOutlined css={/*#__PURE__*/_css({
        color: theme.colors.text.tableHeader
      }, process.env.NODE_ENV === "production" ? "" : ";label:input;")} width={20} height={20} />
						</div>
						{stacked && typeof value === 'string' && value.length > 0 && tokenizeSearchString(_.escapeRegExp(value)).length > 1 ? <HighlightText css={_ref2} text={highlightedText} highlight={highlightedText} stacked /> : null}
					</>} endAdornment={<div css={styles.endAdornment(collapsible, expanded, forceShowEndAdornment)}>
						{barcodeScanner}
						<div onMouseDown={event => {
      if (event.button !== 0) return; // Only handle left-click
      if (handleClear) {
        isClearing.current = true;
        handleClear();
        isClearing.current = false;
      }
    }} css={styles.clearIcon(value, hideClear)} data-testid="clear-search-icon">
							<IconX css={/*#__PURE__*/_css({
        color: theme.colors.text.tableHeader,
        cursor: 'pointer'
      }, process.env.NODE_ENV === "production" ? "" : ";label:input;")} width={20} height={20} />
						</div>
					</div>} placeholder={placeholder ?? t('common:search')} {...inputProps} containerStyle={[styles.container(collapsible, expanded, forceShowEndAdornment, value, customWidth), inputProps.containerStyle]} inputStyle={[styles.input(collapsible, expanded, forceShowEndAdornment), inputProps.inputStyle]} onFocus={e => {
    setExpanded(true);
    inputProps.onFocus?.(e);
  }} onBlur={e => {
    if (isClearing.current) {
      return;
    }
    setExpanded(false);
    inputProps.onBlur?.(e);
  }} onKeyDown={e => {
    if (e.key === 'Enter') {
      setExpanded(false);
    }
    inputProps.onKeyDown?.(e);
  }} />;
  return <div css={styles.positionAnchor(collapsible, forceShowEndAdornment)}>
				<div css={styles.positionContainer(collapsible)}>
					{helperText ? <FormControl helperText={helperText(expanded)} helperStyle={{
        position: 'absolute',
        bottom: '-16px',
        height: '16px',
        left: 16
      }} helperTextStyle={{
        color: theme.colors.text.secondary,
        textAlign: 'right'
      }}>
							{input}
						</FormControl> : input}
				</div>
			</div>;
});